import * as cookie from "cookie";
import { isBrowser } from "@utils/browser";
import { isHttps } from "@utils/env";
import { allLanguages } from "../services/i18n-languages";

/** Shared with Aimoo main site (`aimoo.lychessclub.work`). */
export const AIMOO_LOCALE_COOKIE = "aimoo_locale";

/** Default main-site locale has no URL prefix (e.g. `/market`). */
export const AIMOO_DEFAULT_LOCALE = "zh-CN";

const SUPPORTED = new Set(allLanguages.map(l => l.code));

/** Normalize aliases to a code present in allLanguages. */
export function normalizeAimooLocale(raw?: string | null): string | undefined {
  if (!raw) {
    return undefined;
  }
  const code = raw.trim();
  if (!code) {
    return undefined;
  }
  if (SUPPORTED.has(code)) {
    return code;
  }
  if (code === "zh" || code.startsWith("zh-")) {
    return SUPPORTED.has("zh-CN") ? "zh-CN" : undefined;
  }
  if (code === "pt" || code === "pt_BR") {
    return SUPPORTED.has("pt-BR") ? "pt-BR" : undefined;
  }
  const prefix = code.split("-")[0] ?? code;
  if (SUPPORTED.has(prefix)) {
    return prefix;
  }
  return undefined;
}

/**
 * Main-site URL prefix for the active locale.
 * `zh-CN` → `` ; `en` → `/en` ; `pt-BR` → `/pt-BR`
 */
export function aimooMainSiteLocalePrefix(locale?: string | null): string {
  const code = normalizeAimooLocale(locale) ?? AIMOO_DEFAULT_LOCALE;
  if (code === AIMOO_DEFAULT_LOCALE || code === "zh") {
    return "";
  }
  return `/${code}`;
}

/**
 * Build a main-site href with locale prefix.
 * Examples: `/market` + en → `/en/market` ; `/` + en → `/en`
 */
export function aimooMainSiteHref(
  path: string,
  locale?: string | null,
): string {
  const raw = (path || "/").trim() || "/";
  const hashIdx = raw.indexOf("#");
  const queryIdx = raw.indexOf("?");
  let cut = raw.length;
  if (hashIdx >= 0) {
    cut = Math.min(cut, hashIdx);
  }
  if (queryIdx >= 0) {
    cut = Math.min(cut, queryIdx);
  }
  const pathOnly = raw.slice(0, cut) || "/";
  const suffix = raw.slice(cut);
  const prefix = aimooMainSiteLocalePrefix(locale);

  if (!prefix) {
    return `${pathOnly === "" ? "/" : pathOnly}${suffix}`;
  }
  if (pathOnly === "/" || pathOnly === "") {
    return `${prefix}${suffix}`;
  }
  return `${prefix}${pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`}${suffix}`;
}

export function getAimooLocaleCookie(
  cookieHeaderOrJar?: string | Record<string, string | undefined>,
): string | undefined {
  if (typeof cookieHeaderOrJar === "object" && cookieHeaderOrJar) {
    return normalizeAimooLocale(cookieHeaderOrJar[AIMOO_LOCALE_COOKIE]);
  }
  if (typeof cookieHeaderOrJar === "string") {
    return normalizeAimooLocale(
      cookie.parse(cookieHeaderOrJar)[AIMOO_LOCALE_COOKIE],
    );
  }
  if (isBrowser()) {
    return normalizeAimooLocale(
      cookie.parse(document.cookie)[AIMOO_LOCALE_COOKIE],
    );
  }
  return undefined;
}

export function setAimooLocaleCookie(code: string) {
  if (!isBrowser()) {
    return;
  }
  const normalized = normalizeAimooLocale(code) ?? code;
  document.cookie = cookie.serialize(AIMOO_LOCALE_COOKIE, normalized, {
    path: "/",
    maxAge: 60 * 60 * 24 * 365,
    sameSite: "lax",
    secure: isHttps(),
  });
  try {
    document.documentElement.lang = normalized;
  } catch {
    // ignore
  }
}

/**
 * Resolve UI language for Aimoo:
 * 1) aimoo_locale cookie (shared with main site)
 * 2) logged-in Lemmy interface_language (unless "browser")
 * 3) "browser" → Accept-Language / navigator.languages
 */
export function resolveAimooInterfaceLanguage(
  cookieLocale?: string | null,
  userInterfaceLanguage?: string | null,
): string {
  const fromCookie = normalizeAimooLocale(cookieLocale);
  if (fromCookie) {
    return fromCookie;
  }
  if (
    userInterfaceLanguage &&
    userInterfaceLanguage !== "browser" &&
    normalizeAimooLocale(userInterfaceLanguage)
  ) {
    return normalizeAimooLocale(userInterfaceLanguage)!;
  }
  if (userInterfaceLanguage && userInterfaceLanguage !== "browser") {
    return userInterfaceLanguage;
  }
  return "browser";
}
